/**
 * Phase 5 Deployment Pipeline Settings panel.
 *
 * Operator-facing surface for the /api/integrations/deployment-pipeline
 * routes. Wraps:
 *   - GET status (secret configuration + recent deployments)
 *   - POST /test (fire test repository_dispatch to Gitea)
 *
 * Hygiene rules:
 *   - Secret values are NEVER shown — only 'configured' / 'missing' presence
 *   - Test emit is read-only (no side effects beyond Gitea dispatch event)
 *   - Recent manifests are a rolling list of deployment activity
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { CheckCircle2, XCircle, PlayCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  getPipelineStatus,
  testPipelineEmit,
  type DeploymentPipelineStatusResponse,
} from '@/lib/api';

const QUERY_KEY = ['deploymentPipelineStatus'] as const;

const REQUIRED_SECRETS = [
  'MAESTRO_GITEA_HOST',
  'MAESTRO_GITEA_REPO',
  'MAESTRO_GITEA_TOKEN',
  'MOS_RECEIVER_URL',
  'MOS_RECEIVER_HMAC',
] as const;

interface StatusPillProps {
  status: DeploymentPipelineStatusResponse | undefined;
  isLoading: boolean;
}

/**
 * Status pill colors map to three states:
 * - Ready (green): all 5 secrets configured
 * - Partial (amber): some but not all secrets configured
 * - Unconfigured (gray): no secrets configured
 */
function StatusPill({ status, isLoading }: StatusPillProps): React.ReactElement {
  if (isLoading || !status) {
    return (
      <Badge variant="outline" className="text-xs">
        Loading…
      </Badge>
    );
  }

  if (status.overallStatus === 'ready') {
    return <Badge className="bg-success text-white text-xs hover:bg-success">Ready</Badge>;
  }
  if (status.overallStatus === 'partial') {
    return (
      <Badge className="bg-warning text-white text-xs hover:bg-warning">
        Partial ({Object.values(status.secrets).filter(s => s === 'configured').length}/5)
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="text-xs">
      Unconfigured
    </Badge>
  );
}

interface TestResultSummary {
  dispatched: boolean;
  error?: string;
}

export function DeploymentPipelinePanel(): React.ReactElement {
  const queryClient = useQueryClient();

  const statusQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getPipelineStatus,
    staleTime: 30_000, // 30 seconds
  });

  const testMutation = useMutation({
    mutationFn: testPipelineEmit,
    onSuccess: () => {
      // Refetch status after successful test
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const [testResult, setTestResult] = useState<TestResultSummary | null>(null);

  const handleTestEmit = async (): Promise<void> => {
    setTestResult(null);
    const result = await testMutation.mutateAsync();
    setTestResult({
      dispatched: result.dispatched,
      error: result.error,
    });
  };

  const status = statusQuery.data;
  const missingSecrets = status
    ? Object.entries(status.secrets)
        .filter(([_, s]) => s === 'missing')
        .map(([key]) => key)
    : [];

  return (
    <div className="space-y-4">
      {/* Status card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle>Pipeline Health</CardTitle>
            <StatusPill status={status} isLoading={statusQuery.isLoading} />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-text-secondary">
            Phase 5 deployment pipeline requires 5 environment variables configured on the Maestro
            server.
          </div>

          {/* Secrets grid */}
          <div className="grid grid-cols-1 gap-2 rounded-lg bg-surface-elevated/50 p-3">
            {REQUIRED_SECRETS.map(secret => {
              const configured = status?.secrets[secret] === 'configured';
              return (
                <div key={secret} className="flex items-center gap-2 text-sm">
                  {configured ? (
                    <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-warning shrink-0" />
                  )}
                  <span className="font-mono text-text-secondary">{secret}</span>
                  <Badge variant={configured ? 'outline' : 'secondary'} className="text-xs ml-auto">
                    {configured ? 'Configured' : 'Missing'}
                  </Badge>
                </div>
              );
            })}
          </div>

          {missingSecrets.length > 0 && (
            <div className="rounded-md bg-warning/10 border border-warning/30 p-3 text-sm text-warning">
              <div className="font-medium">Configuration incomplete</div>
              <div className="text-xs mt-1 opacity-80">
                Missing: {missingSecrets.join(', ')}. See the Phase 5 runbook for provisioning
                steps.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test emit card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Test End-to-End</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-text-secondary">
            Fire a test repository_dispatch to Gitea to validate that all 5 secrets are properly
            configured.
          </div>

          <Button
            onClick={() => void handleTestEmit()}
            disabled={status?.overallStatus !== 'ready' || testMutation.isPending}
            className="gap-2"
          >
            <PlayCircle className="w-4 h-4" />
            {testMutation.isPending ? 'Testing…' : 'Test Emit'}
          </Button>

          {testResult && (
            <div
              className={`rounded-md border p-3 text-sm ${
                testResult.dispatched
                  ? 'bg-success/10 border-success/30 text-success'
                  : 'bg-error/10 border-error/30 text-error'
              }`}
            >
              <div className="font-medium">
                {testResult.dispatched ? 'Test successful' : 'Test failed'}
              </div>
              {testResult.error && (
                <div className="text-xs mt-1 opacity-80">{testResult.error}</div>
              )}
            </div>
          )}

          {status?.overallStatus !== 'ready' && (
            <div className="rounded-md bg-warning/10 border border-warning/30 p-3 text-sm text-warning">
              <div className="text-xs">
                Cannot test: all {REQUIRED_SECRETS.length} secrets must be configured.
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent manifests */}
      {status?.recentManifests && status.recentManifests.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent Deployments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {status.recentManifests.slice(0, 5).map(manifest => (
                <div
                  key={manifest.manifestId}
                  className="flex items-center gap-2 text-sm p-2 rounded-md bg-surface-elevated/50"
                >
                  <div className="flex-1">
                    <div className="font-mono text-xs text-text-secondary">
                      {manifest.manifestId}
                    </div>
                    <div className="text-xs text-text-tertiary mt-1">
                      {manifest.outcomeId} → {manifest.environment}
                    </div>
                  </div>
                  <Badge
                    variant={manifest.status === 'succeeded' ? 'outline' : 'secondary'}
                    className="text-xs"
                  >
                    {manifest.status === 'succeeded' ? '✓ Deployed' : '✗ Failed'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Help section */}
      <div className="rounded-md bg-primary/10 border border-primary/30 p-4 text-sm">
        <div className="font-medium text-primary mb-2">Need help?</div>
        <div className="text-xs text-primary space-y-1">
          <p>
            See <span className="font-mono">docs/runbooks/phase5-secrets-provisioning.md</span> for
            step-by-step provisioning instructions.
          </p>
        </div>
      </div>
    </div>
  );
}
